// audio.ts — Web Audio wrapper for the game.
//
// Sound design policy (2026-10, "music is still catastrophic" fix):
//  - There is no sample-based music anymore. The old Defold WAV loops were
//    replaced by a generated ambient pad (slow chords, soft low-pass, very
//    low level) so the background never competes with the game.
//  - Everything runs through one master gain (0.6) into a compressor, so no
//    combination of overlapping sounds can clip into a wall of noise.
//  - Swallow sounds (eat / eat_big) are synthesized: a soft "blup" and a
//    muffled "glub" instead of the old near-peak thumps.
//  - Every effect has its own base level in SFX_LEVEL; callers only pass a
//    relative 0..1 weight.

export type SfxName = 'click' | 'eat' | 'eat_big' | 'end' | 'kill' | 'tick' | 'unlock' | 'whoosh';
export type MusicName = 'menu' | 'play';

// eat / eat_big have no file: they are synthesized (see playSynth).
const SFX_FILES: Partial<Record<SfxName, string>> = {
  click: 'audio/sfx_click.wav',
  end: 'audio/sfx_end.wav',
  kill: 'audio/sfx_kill.wav',
  tick: 'audio/sfx_tick.wav',
  unlock: 'audio/sfx_unlock.wav',
  whoosh: 'audio/sfx_whoosh.wav',
};

/** Overall output level. Deliberately low: the game should feel calm. */
const MASTER_LEVEL = 0.6;
/**
 * Background music sits far below everything else. The generated pad is
 * already normalised to 0.5 peak, so 0.16 here ends up around -26 dBFS.
 */
const MUSIC_LEVEL = 0.16;
/** Minimum spacing between two identical rapid-fire SFX. */
const RAPID_THROTTLE_MS = 120;
const NORMAL_THROTTLE_MS = 45;
const RAPID_NAMES: ReadonlySet<string> = new Set(['eat', 'eat_big']);

/** Base level per effect; multiplied with the caller's relative volume. */
const SFX_LEVEL: Record<SfxName, number> = {
  click: 0.32,
  eat: 1,
  eat_big: 1,
  end: 0.5,
  kill: 0.42,
  tick: 0.28,
  unlock: 0.42,
  whoosh: 0.28,
};

// Ambient pad: a slow four-chord progression (Am – F – C – G), one chord
// every 8 s, cross-faded with Hann windows so the loop is seamless.
const AMBIENT_CHORDS: readonly (readonly number[])[] = [
  [110.0, 130.81, 164.81], // A minor
  [87.31, 110.0, 130.81], // F major
  [130.81, 164.81, 196.0], // C major
  [98.0, 123.47, 146.83], // G major
];
const CHORD_SECONDS = 8;
const WINDOW_SECONDS = 16;
/** Extra render time so every window starts at t >= 0; the tail is dropped. */
const PREROLL_SECONDS = 8;
const AMBIENT_SECONDS = AMBIENT_CHORDS.length * CHORD_SECONDS;

class AudioManager {
  private ctx?: AudioContext;
  private buffers = new Map<string, AudioBuffer>();
  private musicGain?: GainNode;
  private musicFilter?: BiquadFilterNode;
  private musicSource?: AudioBufferSourceNode;
  private master?: GainNode;
  private lastPlayed = new Map<string, number>();
  private musicName: MusicName | null = null;
  private ambient?: AudioBuffer;
  private ambientJob?: Promise<AudioBuffer | undefined>;
  muted = false;

  /** Must be called from a user gesture at least once (autoplay policy). */
  ensure(): AudioContext {
    if (!this.ctx) {
      const Ctor: typeof AudioContext =
        window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
      // master -> compressor -> destination: overlapping SFX are limited
      // before they can clip.
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : MASTER_LEVEL;
      const compressor = this.ctx.createDynamicsCompressor();
      compressor.threshold.value = -20;
      compressor.knee.value = 24;
      compressor.ratio.value = 8;
      compressor.attack.value = 0.004;
      compressor.release.value = 0.18;
      this.master.connect(compressor);
      compressor.connect(this.ctx.destination);
      // Music bus sits under the SFX bus and is low-passed: muffled, distant,
      // never sharp.
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = this.muted ? 0 : MUSIC_LEVEL;
      this.musicFilter = this.ctx.createBiquadFilter();
      this.musicFilter.type = 'lowpass';
      this.musicFilter.frequency.value = 1400;
      this.musicFilter.Q.value = 0.4;
      this.musicGain.connect(this.musicFilter);
      this.musicFilter.connect(this.master);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  setMuted(muted: boolean) {
    this.muted = muted;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(muted ? 0 : MASTER_LEVEL, this.ctx.currentTime, 0.02);
    }
  }

  private async bufferFor(url: string): Promise<AudioBuffer | undefined> {
    const cached = this.buffers.get(url);
    if (cached) return cached;
    try {
      const response = await fetch(url);
      if (!response.ok) return undefined;
      const bytes = await response.arrayBuffer();
      const buffer = await this.ensure().decodeAudioData(bytes);
      this.buffers.set(url, buffer);
      return buffer;
    } catch {
      return undefined;
    }
  }

  /**
   * Play a sound effect. `volume` is a relative 0..1 weight; rapid-fire names
   * (eat, eat_big) are throttled so a fast eating streak cannot stack into a
   * loud buzz. `rate` varies the pitch of file-based SFX (1 = normal).
   */
  async playSfx(name: SfxName, volume = 1, rate = 1): Promise<void> {
    if (this.muted) return;
    const now = performance.now();
    const last = this.lastPlayed.get(name) ?? 0;
    const gap = RAPID_NAMES.has(name) ? RAPID_THROTTLE_MS : NORMAL_THROTTLE_MS;
    if (now - last < gap) return;
    this.lastPlayed.set(name, now);
    try {
      const ctx = this.ensure();
      const file = SFX_FILES[name];
      if (!file) {
        // Only eat/eat_big have no file entry (see SFX_FILES).
        this.playSynth(name as 'eat' | 'eat_big', volume);
        return;
      }
      const buffer = await this.bufferFor(file);
      if (!buffer || !this.master) return;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = rate;
      const gain = ctx.createGain();
      gain.gain.value = Math.min(1, volume) * SFX_LEVEL[name];
      source.connect(gain).connect(this.master);
      source.start();
    } catch {
      // Audio is decorative; gameplay must not depend on it.
    }
  }

  /**
   * Synthesized swallow sounds — quiet, muffled, never harsh:
   *  - eat:     short soft "blup" (~330 → ~210 Hz, 90 ms)
   *  - eat_big: deep muffled "glub" (~115 → ~52 Hz, 220 ms) plus a tiny
   *             band-passed noise "suction" at the start
   * Both use a soft 8 ms attack, an exponential decay, a low-pass filter and
   * a per-play ±7 % pitch wobble. Peak stays far below the compressor
   * threshold, so even a long eating streak stays unobtrusive.
   */
  private playSynth(name: 'eat' | 'eat_big', volume: number): void {
    const ctx = this.ensure();
    if (!this.master) return;
    const isBig = name === 'eat_big';
    const t0 = ctx.currentTime + 0.005;
    const wobble = 0.93 + Math.random() * 0.14;
    const f0 = (isBig ? 115 : 330) * wobble;
    const f1 = (isBig ? 52 : 210) * wobble;
    const dur = isBig ? 0.22 : 0.09;
    const peak = Math.min(0.18, volume * (isBig ? 0.34 : 0.26));

    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(0, t0);
    envelope.gain.linearRampToValueAtTime(peak, t0 + 0.008);
    envelope.gain.exponentialRampToValueAtTime(0.0005, t0 + dur);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = isBig ? 380 : 900;
    filter.Q.value = 0.8;
    envelope.connect(filter);
    filter.connect(this.master);

    // Main tone plus a quiet detuned partner: keeps it from sounding like
    // a pure beep while staying soft.
    const stopAt = t0 + dur + 0.02;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(f0, t0);
    osc.frequency.exponentialRampToValueAtTime(f1, t0 + dur * 0.8);
    osc.connect(envelope);
    osc.start(t0);
    osc.stop(stopAt);
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(f0 * 1.012, t0);
    osc2.frequency.exponentialRampToValueAtTime(f1 * 1.012, t0 + dur * 0.8);
    const partner = ctx.createGain();
    partner.gain.value = 0.35;
    osc2.connect(partner);
    partner.connect(envelope);
    osc2.start(t0);
    osc2.stop(stopAt);

    if (isBig) {
      // Tiny filtered-noise "suction" at the very start of the gulp.
      const noise = ctx.createBufferSource();
      const length = Math.floor(ctx.sampleRate * 0.05);
      const buf = ctx.createBuffer(1, length, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length);
      noise.buffer = buf;
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.value = 500;
      band.Q.value = 1.2;
      const noiseGain = ctx.createGain();
      noiseGain.gain.value = peak * 0.25;
      noise.connect(band);
      band.connect(noiseGain);
      noiseGain.connect(this.master);
      noise.start(t0);
    }
  }

  /**
   * Render the ambient pad once. A 40 s buffer is rendered offline and the
   * first 8 s are dropped, so the returned 32 s loop is seamless (the Hann
   * cross-fade windows are periodic with the loop length).
   */
  private async ambientBuffer(ctx: AudioContext): Promise<AudioBuffer | undefined> {
    if (this.ambient) return this.ambient;
    if (this.ambientJob) return this.ambientJob;
    this.ambientJob = (async () => {
      const OfflineCtor = (window as unknown as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext;
      if (!OfflineCtor) return undefined;
      try {
        const rate = ctx.sampleRate;
        const total = PREROLL_SECONDS + AMBIENT_SECONDS;
        const offline = new OfflineCtor(1, Math.floor(rate * total), rate);

        const bus = offline.createGain();
        bus.gain.value = 0.5;
        const lowpass = offline.createBiquadFilter();
        lowpass.type = 'lowpass';
        lowpass.frequency.value = 900;
        lowpass.Q.value = 0.5;
        bus.connect(lowpass);
        lowpass.connect(offline.destination);

        // Slow filter movement: 2 cycles per loop, so it stays periodic.
        const lfo = offline.createOscillator();
        lfo.frequency.value = 2 / AMBIENT_SECONDS;
        const lfoDepth = offline.createGain();
        lfoDepth.gain.value = 220;
        lfo.connect(lfoDepth);
        lfoDepth.connect(lowpass.frequency);
        lfo.start(0);

        // Hann window over WINDOW_SECONDS; two windows always overlap and
        // their sum is 1, which makes the chord cross-fade click-free.
        const curve = new Float32Array(65);
        for (let i = 0; i <= 64; i++) curve[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / 64);

        const scheduleChord = (chord: readonly number[], start: number) => {
          chord.forEach((frequency, voice) => {
            for (const [cents, level] of [[-5, 1], [5, 0.65]] as const) {
              const osc = offline.createOscillator();
              osc.type = voice === 0 ? 'sine' : 'triangle';
              osc.frequency.value = frequency * Math.pow(2, cents / 1200);
              const gain = offline.createGain();
              gain.gain.setValueAtTime(0, start);
              gain.gain.setValueCurveAtTime(curve, start, WINDOW_SECONDS);
              gain.gain.setValueAtTime(0, start + WINDOW_SECONDS);
              const voiceGain = offline.createGain();
              voiceGain.gain.value = 0.09 * level * (voice === 0 ? 1.1 : 0.8);
              osc.connect(gain);
              gain.connect(voiceGain);
              voiceGain.connect(bus);
              osc.start(start);
              osc.stop(start + WINDOW_SECONDS + 0.1);
            }
          });
          // Sub octave: adds warmth without volume.
          const sub = offline.createOscillator();
          sub.type = 'sine';
          sub.frequency.value = chord[0]! * 0.5;
          const subGain = offline.createGain();
          subGain.gain.setValueAtTime(0, start);
          subGain.gain.setValueCurveAtTime(curve, start, WINDOW_SECONDS);
          subGain.gain.setValueAtTime(0, start + WINDOW_SECONDS);
          const subLevel = offline.createGain();
          subLevel.gain.value = 0.12;
          sub.connect(subGain);
          subGain.connect(subLevel);
          subLevel.connect(bus);
          sub.start(start);
          sub.stop(start + WINDOW_SECONDS + 0.1);
        };

        // One extra cycle of windows so the loop boundary is continuous.
        for (let cycle = -1; cycle <= 1; cycle++) {
          AMBIENT_CHORDS.forEach((chord, index) => {
            const start = PREROLL_SECONDS + index * CHORD_SECONDS + cycle * AMBIENT_SECONDS - CHORD_SECONDS;
            if (start < 0 || start > total) return;
            scheduleChord(chord, start);
          });
        }

        const rendered = await offline.startRendering();
        const source = rendered.getChannelData(0);
        const offset = Math.floor(PREROLL_SECONDS * rate);
        const length = Math.floor(AMBIENT_SECONDS * rate);
        const loop = ctx.createBuffer(1, length, rate);
        const target = loop.getChannelData(0);
        let peak = 0;
        for (let i = 0; i < length; i++) {
          const value = source[offset + i] ?? 0;
          target[i] = value;
          const abs = Math.abs(value);
          if (abs > peak) peak = abs;
        }
        // Normalise to 0.5 peak so MUSIC_LEVEL has a predictable meaning.
        if (peak > 0.0001) {
          const scale = 0.5 / peak;
          for (let i = 0; i < length; i++) target[i] = target[i]! * scale;
        }
        this.ambient = loop;
        return loop;
      } catch {
        return undefined;
      }
    })();
    return this.ambientJob;
  }

  async playMusic(name: MusicName): Promise<void> {
    if (this.musicName === name && this.musicSource) return;
    try {
      const ctx = this.ensure();
      this.stopMusic();
      const buffer = await this.ambientBuffer(ctx);
      if (!buffer || !this.musicGain) return;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      // Gameplay keeps the pad a touch quieter than the menus.
      this.musicGain.gain.value = this.muted ? 0 : MUSIC_LEVEL * (name === 'play' ? 0.8 : 1);
      source.connect(this.musicGain);
      source.start();
      this.musicSource = source;
      this.musicName = name;
    } catch {
      // ignore
    }
  }

  stopMusic() {
    this.musicSource?.stop();
    this.musicSource?.disconnect();
    this.musicSource = undefined;
    this.musicName = null;
  }
}

export const audio = new AudioManager();
